import { Spinning } from '@components/Spinning';
import { colors } from '@components/theme';
import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { EOrderDirection, OrderValue } from '@models/table';
import React, { ReactNode, useMemo } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { SwyftTableCell, SwyftTableInnerCell } from './components/TableCell';
import { SwyftTableCol } from './components/TableCol';
import { SwyftTableHeader, SwyftTableInnerHeader } from './components/TableHeader';
import { SwyftTablePagination } from './components/TablePagination';
import { Column } from './models/types';

type SwyftTableProps = {
  rows: any[];
  rowId: string;
  loading?: boolean;
  totalRows?: number;
  pageSize?: number | null;
  pageIndex?: number | null;
  hideHeader?: boolean;
  hidePagination?: boolean;
  sortField?: string | null;
  sortDirection?: EOrderDirection | string | null;
  onPageIndexChange?: (index: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSortChange?: (value: OrderValue) => void;
  selectable?: boolean;
  selectedIds?: string[];
  onChangeSelectedIds?: (selectedIds: string[]) => void;
  noItemIcon?: ReactNode;
  noItemText?: string;
  isSearch?: boolean;
  children?: ReactNode;
  minWidth?: string | number;
  onClickRow?: (row: any) => void;
  noResultSearchText?: string;
  showHeaderWhenNoItem?: boolean;
};

export const SwyftTable = ({
  rows = [],
  rowId = 'id',
  loading = false,
  totalRows,
  pageSize,
  pageIndex,
  hideHeader = false,
  hidePagination = false,
  selectable = false,
  sortField = '',
  sortDirection = '',
  selectedIds = [],
  onPageIndexChange = () => null,
  onPageSizeChange = () => null,
  onSortChange,
  onChangeSelectedIds,
  noItemIcon,
  noItemText,
  isSearch,
  children,
  minWidth,
  onClickRow,
  noResultSearchText,
  showHeaderWhenNoItem = false
}: SwyftTableProps): JSX.Element => {
  const columns: Column[] = useMemo(() => {
    return React.Children.toArray(children)
      .filter((c) => (c as any).type === SwyftTableCol)
      .map((c: any) => {
        const {
          props: { children: colChildren, ...rest }
        } = c;

        const cellChild: any = React.Children.toArray(colChildren).find((c: any) => c.type === SwyftTableCell);
        const headerChild: any = React.Children.toArray(colChildren).find((c: any) => c.type === SwyftTableHeader);

        return {
          ...rest,
          templateCell: cellChild ? cellChild.props.children : null,
          templateHeader: headerChild ? headerChild.props.children : null
        };
      });
  }, [children]);

  const sortHandler = (property: string) => {
    const isAsc = sortField === property && sortDirection === 'asc';
    if (onSortChange) {
      onSortChange({
        orderBy: property,
        orderDirection: isAsc ? EOrderDirection.Descending : EOrderDirection.Ascending
      });
    }
  };

  const isSelected = (name: string) => selectedIds?.indexOf(name) !== -1;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChangeSelectedIds) {
      if (event.target.checked) {
        onChangeSelectedIds(rows.map((r) => r[rowId]));
        return;
      }
      onChangeSelectedIds([]);
    }
  };

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
    if (onChangeSelectedIds) {
      const selectedIndex = selectedIds.indexOf(name);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedIds, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedIds.slice(1));
      } else if (selectedIndex === selectedIds.length - 1) {
        newSelected = newSelected.concat(selectedIds.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(selectedIds.slice(0, selectedIndex), selectedIds.slice(selectedIndex + 1));
      }

      onChangeSelectedIds(newSelected);
    }
  };

  if (!isSearch && !loading && !rows.length && !showHeaderWhenNoItem) {
    return (
      <Box textAlign={'center'} width={'100%'} py={16}>
        {noItemIcon}
        <Typography variant='subtitle1' style={{ color: colors.grey[100] }}>
          {noItemText}
        </Typography>
      </Box>
    );
  }

  if (isSearch && !loading && !totalRows) {
    return (
      <Box textAlign={'center'} py={16} style={{ opacity: 0.3 }}>
        <Search className='extra-large' />
        <Typography variant='h6'>
          {noResultSearchText ? noResultSearchText : "We are sorry, we didn't find any results"}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Spinning spinning={loading}>
        <Box border={`1px solid ${colors.grey[50]}`} borderBottom='none' bgcolor={colors.white}>
          <PerfectScrollbar>
            <div style={{ minWidth: minWidth ? minWidth : 1050 }}>
              <Table className='swyft-table'>
                {!hideHeader && (
                  <TableHead>
                    <TableRow>
                      {selectable && (
                        <TableCell padding='checkbox'>
                          <Checkbox
                            indeterminate={selectedIds?.length > 0 && selectedIds?.length < rows.length}
                            checked={selectedIds?.length === rows.length}
                            onChange={handleSelectAllClick}
                            color='primary'
                          />
                        </TableCell>
                      )}
                      {columns.map((col) => (
                        <SwyftTableInnerHeader
                          key={col.name}
                          col={col}
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSortChange={sortHandler}
                          width={col.width}
                          align={col.align}
                        />
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow hover key={row[rowId]}>
                      {selectable && (
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={isSelected(row[rowId])}
                            onChange={(e) => handleSelect(e, row[rowId])}
                            color='primary'
                          />
                        </TableCell>
                      )}
                      {columns.map((col) => (
                        <TableCell
                          key={col.name}
                          onClick={() => (!col.disableClickAction && onClickRow ? onClickRow(row) : null)}
                        >
                          {!col.templateCell && (
                            <SwyftTableInnerCell data={row[col.name]} type={col.type} align={col.align} />
                          )}
                          {col.templateCell &&
                            (typeof col.templateCell !== 'function' ? col.templateCell : col.templateCell(row, index))}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </Box>
      </Spinning>
      {!hidePagination && (
        <Box px={3} py={2}>
          <SwyftTablePagination
            totalRows={totalRows}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageIndexChange={onPageIndexChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Box>
      )}
    </React.Fragment>
  );
};
